import { useState, useMemo, useRef, useEffect } from 'react'
import styled, { useTheme } from 'styled-components'
import { Modal, Form, Select, DatePicker, Tooltip } from 'antd'
import { MinusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { FormCard, FormItem, Heading, Input, TextArea } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { showToast } from 'common/utils'
import Disposal from '../iconsV2/Disposal'
import { Button, Text } from '@lmc/ui-kit/src/general'
import QuestionMarkIcon from '@lmc/ui-kit/src/general/MoreInfoLink/QuestionMarkIcon'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import Asterix from 'screens/Rostering/RotaPattern/components/Asterix'
import StockBanner from './StockBanner'
import AdditionalDetailsSection from './AdditionalDetailsSection'
import { useDisposal } from '../hooks/useDisposal'
import { useGroupedCarers } from 'hooks/useGroupedOptions'
import { StockRecord, DisposalData } from '../types'
import { convertMediaAttachmentsToFileList } from '../utils/stockRecordUtils'

type DisposalModalProps = {
  open: boolean
  medicationName: string
  expectedStock: number
  residentId: string
  residentMedicationId: string
  onCancel: () => void
  onSuccess?: () => void
  editMode?: boolean
  record?: StockRecord
}

const DisposalModal = ({
  open,
  medicationName,
  expectedStock,
  residentId,
  residentMedicationId,
  onCancel,
  onSuccess,
  editMode = false,
  record
}: DisposalModalProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const theme = useTheme()
  const [form] = Form.useForm()

  // Watch discrepancyReason field to reactively update button state
  const discrepancyReason = Form.useWatch('discrepancyReason', form)

  const getInitialData = (record: StockRecord | undefined) => {
    if (!record) return undefined
    const description = Array.isArray(record.notes)
      ? record.notes.join(', ')
      : record.notes || ''
    const newStock = record.stockCount ?? record.resultingStockLevel ?? 0

    // Calculate expected stock: use previousStockLevel (stock before disposal) if available
    // Otherwise fall back to expectedStock, or calculate from newStock + disposed amount
    let calculatedExpectedStock =
      record.previousStockLevel ?? record.expectedStock
    if (
      calculatedExpectedStock === undefined &&
      newStock !== undefined &&
      record.quantityChange !== undefined
    ) {
      // newStock = previousStockLevel - disposedStock
      // So: previousStockLevel = newStock + disposedStock
      calculatedExpectedStock = newStock + Math.abs(record.quantityChange)
    }
    calculatedExpectedStock = calculatedExpectedStock ?? 0

    return {
      recordId: record.id,
      description,
      disposedStock: Math.abs(record.quantityChange),
      newStock,
      expectedStock: calculatedExpectedStock,
      hasDiscrepancy: record.hasDiscrepancy ?? false,
      discrepancyReason:
        record.discrepancy?.reason || record.discrepancy?.carerReason || '',
      actionedBy: record.disposedBy?.id || '',
      actionTime: record.actionTime ? dayjs(record.actionTime) : undefined,
      disposalMethod: record.disposalMethod || '',
      additionalDetails: false
    }
  }

  const initialData = getInitialData(record)

  // In edit mode, use the record's calculated expectedStock (from previousStockLevel); otherwise use the prop
  const effectiveExpectedStock =
    editMode && initialData?.expectedStock !== undefined
      ? initialData.expectedStock
      : expectedStock

  const initialValues = {
    description: initialData?.description || '',
    actionedBy: initialData?.actionedBy || undefined,
    actionTime: initialData?.actionTime,
    disposalMethod: initialData?.disposalMethod || '',
    discrepancyReason: initialData?.discrepancyReason || ''
  }

  const [disposedStock, setDisposedStock] = useState<number | null>(
    editMode && initialData?.disposedStock !== undefined
      ? initialData.disposedStock
      : null
  )

  const hasMedia = editMode && record?.mediaAttachments?.length

  const [fileList, setFileList] = useState<any[]>(() => {
    if (!hasMedia) return []
    return convertMediaAttachmentsToFileList(
      record!.mediaAttachments!,
      record!.id
    )
  })

  const [showAdditionalDetails, setShowAdditionalDetails] = useState(!!hasMedia)
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null)

  // Track initial values for dirty detection
  const initialFileListRef = useRef<any[]>([])
  const initialShowAdditionalDetailsRef = useRef<boolean>(false)
  const initialDisposedStockRef = useRef<number | null>(null)
  const prevOpenRef = useRef<boolean>(false)

  useEffect(() => {
    // Only capture initial values when modal transitions from closed to open
    if (open && !prevOpenRef.current) {
      // Deep copy fileList to avoid reference issues
      initialFileListRef.current = fileList.map((f) => ({ ...f }))
      initialShowAdditionalDetailsRef.current = showAdditionalDetails
      initialDisposedStockRef.current = disposedStock
    }
    prevOpenRef.current = open
  }, [open])

  const { groupedCarerOptions } = useGroupedCarers()
  const carerOptions = useMemo(() => {
    return groupedCarerOptions.flatMap((group) => group.options)
  }, [groupedCarerOptions])

  const { createDisposal, updateDisposal, isLoading } = useDisposal({
    residentId,
    residentMedicationId,
    onSuccess: () => {
      showToast({
        type: 'success',
        title: editMode
          ? t('DISPOSAL_UPDATED_SUCCESS')
          : t('DISPOSAL_CREATED_SUCCESS')
      })
      onSuccess?.()
    },
    onError: () => {
      showToast({
        type: 'error',
        title: t('DISPOSAL_ERROR')
      })
    }
  })

  const handleFileChange = (info: any) => {
    setFileList(info.fileList)
  }

  const handleDisposedStockChange = (value: string) => {
    if (!value || value === '') {
      setDisposedStock(null)
      setAdjustmentError(null)
      return
    }

    const numVal = Number(value)
    if (isNaN(numVal)) return

    if (numVal <= 0) {
      setAdjustmentError(t('INVALID_INPUT'))
      setDisposedStock(null)
    } else if (numVal > expectedStock) {
      setAdjustmentError(t('DISPOSAL_TOO_HIGH'))
      setDisposedStock(numVal)
    } else {
      setAdjustmentError(null)
      setDisposedStock(numVal)
    }
  }

  const handleSave = () => {
    form.validateFields().then((values) => {
      const finalDisposedStock = disposedStock ?? 0
      // In edit mode, preserve the original discrepancy status unless user changes disposed stock
      const hasDiscrepancy =
        editMode && disposedStock === initialData?.disposedStock
          ? (initialData?.hasDiscrepancy ?? false)
          : finalDisposedStock > effectiveExpectedStock
      const newStock = hasDiscrepancy
        ? 0
        : effectiveExpectedStock - finalDisposedStock

      const mediaFiles = fileList
        .map((file: any) => file.originFileObj)
        .filter(Boolean) as File[]

      const currentFileKeys = new Set(
        fileList
          .filter((file: any) => !file.originFileObj && file.key)
          .map((file: any) => file.key)
      )
      const removedKeys =
        editMode && record?.mediaAttachments
          ? record.mediaAttachments
              .filter((attachment) => !currentFileKeys.has(attachment.key))
              .map((attachment) => attachment.key)
          : []

      const data: DisposalData = {
        description: values.description || '',
        disposedStock: finalDisposedStock,
        newStock,
        hasDiscrepancy,
        discrepancyReason: hasDiscrepancy ? values.discrepancyReason || '' : '',
        actionedBy: values.actionedBy || '',
        actionTime: values.actionTime || undefined,
        disposalMethod: values.disposalMethod || '',
        additionalDetails: showAdditionalDetails,
        media: mediaFiles,
        mediaAttachmentKeysToRemove:
          removedKeys.length > 0 ? removedKeys : undefined
      }

      if (editMode && record?.id) {
        updateDisposal({ recordId: record.id, data })
      } else {
        createDisposal(data)
      }
    })
  }

  // In edit mode, use the record's hasDiscrepancy flag instead of recalculating
  const hasWarning = editMode
    ? (initialData?.hasDiscrepancy ?? false)
    : disposedStock !== null && disposedStock > effectiveExpectedStock

  // In edit mode, use the record's actual stock count
  const newStock =
    editMode && initialData?.newStock !== undefined
      ? initialData.newStock
      : disposedStock !== null && disposedStock > 0
        ? hasWarning
          ? 0
          : effectiveExpectedStock - disposedStock
        : null

  const discrepancyAmount = hasWarning
    ? editMode && initialData?.hasDiscrepancy
      ? (record?.discrepancy?.amount ?? null)
      : (disposedStock ?? 0) - effectiveExpectedStock
    : null
  const hasError = adjustmentError !== null && !hasWarning
  const newStockValue =
    disposedStock === null || disposedStock === 0 ? '' : newStock

  // Check if form is dirty
  const isFormDirty = () => {
    // Check if form fields have been touched
    const isFieldsTouched = form.isFieldsTouched()

    // Check if state values have changed
    const disposedStockChanged =
      disposedStock !== initialDisposedStockRef.current
    const additionalDetailsChanged =
      showAdditionalDetails !== initialShowAdditionalDetailsRef.current

    // Check if file list has changed (compare by length and file keys/names)
    const fileListChanged = (() => {
      if (fileList.length !== initialFileListRef.current.length) return true
      const currentKeys = fileList
        .map((f) => f.uid || f.name || f.key)
        .filter(Boolean)
      const initialKeys = initialFileListRef.current
        .map((f) => f.uid || f.name || f.key)
        .filter(Boolean)
      if (currentKeys.length !== initialKeys.length) return true
      return currentKeys.some((key, idx) => key !== initialKeys[idx])
    })()

    // Check if form values differ from initial values
    const formValues = form.getFieldsValue()
    const valuesChanged = Object.keys(formValues).some((key) => {
      const currentValue = formValues[key]
      const initialValue = initialValues[key as keyof typeof initialValues]

      // Handle dayjs date comparison
      if (dayjs.isDayjs(currentValue) && dayjs.isDayjs(initialValue)) {
        return !currentValue.isSame(initialValue)
      }
      if (dayjs.isDayjs(currentValue) && !initialValue) return true
      if (!currentValue && dayjs.isDayjs(initialValue)) return true

      return currentValue !== initialValue
    })

    return (
      isFieldsTouched ||
      disposedStockChanged ||
      fileListChanged ||
      additionalDetailsChanged ||
      valuesChanged
    )
  }

  const handleCancel = () => {
    if (isFormDirty()) {
      Modal.confirm({
        title: t('MODAL_TITLE', { ns: NAMESPACE.GENERAL }),
        content: t('MODAL_CONTENT', { ns: NAMESPACE.GENERAL }),
        okText: t('EXIT_WITHOUT_SAVING', { ns: NAMESPACE.GENERAL }),
        cancelText: t('CANCEL'),
        onOk: () => {
          form.resetFields()
          onCancel()
        }
      })
    } else {
      onCancel()
    }
  }

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={648}
      title={
        <TitleContainer>
          <Disposal />
          <ModalTitle>
            <Heading level={3}>
              {editMode ? t('EDIT_DISPOSAL') : t('DISPOSAL')}
            </Heading>
          </ModalTitle>
        </TitleContainer>
      }
    >
      <Divider />

      <Form form={form} initialValues={initialValues} preserve={false}>
        <ModalContentContainer>
          <FormCard level={2}>
            <FormItem
              name="description"
              label={<Text>{t('DESCRIPTION')}</Text>}
              required
              full
              rules={[
                { required: true, message: t('DESCRIPTION_REQUIRED') },
                { max: 150, message: t('DESCRIPTION_MAX_LENGTH') }
              ]}
            >
              <StyledTextArea
                rows={4}
                placeholder={t('ENTER_DESCRIPTION')}
                maxLength={150}
                showCount
                data-testid="disposal-description"
              />
            </FormItem>
          </FormCard>

          <FormCard
            level={2}
            title={
              <CardTitleContainer>
                <MedicationNameHeading weight="bold" level={5}>
                  {medicationName}
                </MedicationNameHeading>
              </CardTitleContainer>
            }
          >
            <AdjustmentContainer>
              <CalculationContainer>
                <CalculationColumn $align="center" $width="140px">
                  <Label>{t('EXPECTED_STOCK')}</Label>
                  <Value weight="bold">
                    {effectiveExpectedStock} {record?.unit}
                  </Value>
                </CalculationColumn>
                <MinusIconWrapper>
                  <MinusOutlined />
                </MinusIconWrapper>
                <CalculationColumn $flex>
                  <LabelContainer>
                    <Asterix />
                    <Label>{t('DISPOSED_STOCK')}</Label>
                  </LabelContainer>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={disposedStock ?? ''}
                    onChange={(e) => handleDisposedStockChange(e.target.value)}
                    disabled={editMode}
                    style={{ width: '100%' }}
                    data-testid="disposed-stock-input"
                    suffix={record?.unit}
                  />
                </CalculationColumn>
                <EqualsIconWrapper>
                  <EqualsSign>=</EqualsSign>
                </EqualsIconWrapper>
                <CalculationColumn $align="center" $width="140px">
                  <Label>{t('NEW_STOCK')}</Label>
                  <DiscrepancyContainer>
                    <NewStockValue weight="bold">
                      {newStockValue} {record?.unit}
                    </NewStockValue>
                    {hasWarning && discrepancyAmount !== null && !editMode && (
                      <DiscrepancyValueUnderNewStock level={2}>
                        {discrepancyAmount} {t('DISCREPANCY')}
                      </DiscrepancyValueUnderNewStock>
                    )}
                  </DiscrepancyContainer>
                </CalculationColumn>
              </CalculationContainer>
              {hasError && !editMode && (
                <StockBanner type="error">{adjustmentError}</StockBanner>
              )}
              {hasWarning && (
                <>
                  {!editMode && (
                    <StockBanner type="warning">
                      {t('DISPOSAL_TOO_HIGH')}
                    </StockBanner>
                  )}
                  <FormItem
                    name="discrepancyReason"
                    label={<Text>{t('REASON_FOR_DISCREPANCY')}</Text>}
                    required={!editMode || initialData?.hasDiscrepancy}
                    full
                    rules={[
                      {
                        required: !editMode || initialData?.hasDiscrepancy,
                        message: t('REASON_FOR_DISCREPANCY_REQUIRED')
                      }
                    ]}
                  >
                    <TextArea
                      rows={4}
                      placeholder={t('ENTER_REASON')}
                      maxLength={150}
                      showCount
                    />
                  </FormItem>
                </>
              )}
            </AdjustmentContainer>
          </FormCard>

          <FormCard level={2}>
            <FormItem
              name="actionedBy"
              label={<Text>{t('ACTIONED_BY')}</Text>}
              required
              rules={[{ required: true, message: t('ACTIONED_BY_REQUIRED') }]}
            >
              <Select
                placeholder={t('SELECT_PERSON')}
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {carerOptions.map((option) => (
                  <Select.Option
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  >
                    {option.image ? (
                      <OptionContainer>
                        <ImageContainer>{option.image}</ImageContainer>
                        <StyledText>{option.label}</StyledText>
                      </OptionContainer>
                    ) : (
                      option.label
                    )}
                  </Select.Option>
                ))}
              </Select>
            </FormItem>

            <FormItem
              name="actionTime"
              label={<Text>{t('ACTIONED_DATE')}</Text>}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder={t('SELECT_DATE')}
                format="DD-MM-YYYY"
              />
            </FormItem>

            <FormItem
              name="disposalMethod"
              label={
                <LabelWithIcon>
                  <Text>{t('DISPOSAL_METHOD')}</Text>
                  <Tooltip
                    title={t('DISPOSAL_METHOD_TOOLTIP')}
                    color={theme.color.neutral.color3}
                    overlayInnerStyle={{
                      color: '#fff',
                      borderRadius: '9px',
                      padding: '10px 16px'
                    }}
                  >
                    <QuestionIcon>
                      <QuestionMarkIcon />
                    </QuestionIcon>
                  </Tooltip>
                </LabelWithIcon>
              }
            >
              <Input placeholder={t('TYPE_HERE')} />
            </FormItem>
          </FormCard>

          <AdditionalDetailsSection
            showAdditionalDetails={showAdditionalDetails}
            onToggle={setShowAdditionalDetails}
            fileList={fileList}
            onFileChange={handleFileChange}
          />
        </ModalContentContainer>
      </Form>

      <Divider />

      <ButtonsContainer>
        <Button onClick={handleCancel} data-testid="cancel-btn">
          {t('CANCEL')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={
            isLoading ||
            disposedStock === null ||
            disposedStock === 0 ||
            (hasWarning && !discrepancyReason?.trim())
          }
          isLoading={isLoading}
          data-testid="save-btn"
        >
          {t('SAVE')}
        </Button>
      </ButtonsContainer>
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`

const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const MedicationNameHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CardTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const CalculationContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const CalculationColumn = styled.div<{
  $align?: 'left' | 'center'
  $width?: string
  $flex?: boolean
}>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 0 8px;
  gap: 8px;
  align-items: ${({ $align = 'left' }) => $align};
  ${({ $width }) => $width && `width: ${$width};`}
  ${({ $flex }) => $flex && `flex: 1;`}
`

const IconWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;
  padding-top: 24px;
`

const MinusIconWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;
  padding-top: 24px;
  width: 18px;
  height: 18px;

  .anticon {
    font-size: 18px;
    color: ${({ theme }) => theme.color.neutral.color1};
  }
`

const EqualsIconWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;
  padding-top: 24px;
`

const EqualsSign = styled(Text)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.neutral.color1};
`

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const LabelWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const QuestionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.neutral.color1};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`

const Label = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  min-height: 24px;
`

const Value = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const NewStockValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1} !important;
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const DiscrepancyValue = styled(Text)<{
  $isPositive: boolean
  $noDiscrepancy?: boolean
}>`
  color: ${({ $noDiscrepancy, theme }) =>
    $noDiscrepancy ? theme.color.status.success : theme.color.status.warning};
  align-self: center;
  display: flex;
  align-items: center;
`

const DiscrepancyValueUnderNewStock = styled(Text)`
  color: ${({ theme }) => theme.color.status.warning};
`

const DiscrepancyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledTextArea = styled(TextArea)`
  width: 100%;
  margin-bottom: 16px;
`

const AdjustmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ButtonsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: normal;
  word-break: break-word;
`

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

export default DisposalModal
